// @sb-ai-platform/schemas — agent-config `memory:` access-map parser (fn-80)
//
// The SINGLE shared runtime definition of the per-agent memory access map:
// the closed scope/mode enumerations + the `parseMemoryAccessMap` normalizer.
// Two fail-closed layers consume it — the config-repo scanner's
// `validateConfig` (deploy-time, fn-80.1) and the session-create pinned-map
// backstop (fn-80.3) — and BOTH import the PARSER from here, never a
// re-listed enum, so the layers cannot drift.
//
// Naming: this agent-config `memory:` ACCESS MAP (scope → access mode,
// authored in the agent's config-repo YAML) is distinct from the session
// `memory` REQUEST field (`POST /sessions` — an array of requested scope
// names, normalized by `./session-memory.ts`). Two different things that
// share a key name.
//
// **Boundary discipline**: lives in `schemas`; imports nothing from any impl
// package. The scope/mode unions are string-aligned with the provider seam's
// `SessionMemoryScope` / `SessionMemoryAccessMode`
// (`packages/provider/src/index.ts`) — mirrored, not imported (`schemas`
// depends on neither); a compile-time assertion in the control plane
// (`packages/control-plane/src/agents/memory-access-seam-compat.ts`) pins the
// unions exactly compatible so the parser and the seam cannot drift.
/**
 * The closed set of memory access scopes, in CANONICAL ORDER
 * (`user`, `firm`, `matter`). String-aligned with `SESSION_MEMORY_SCOPES`
 * (`./session-memory.ts`) and the provider seam's `SessionMemoryScope`.
 */
export const MEMORY_ACCESS_SCOPES = ['user', 'firm', 'matter'];
/**
 * The closed set of memory access modes. String-aligned with the provider
 * seam's `SessionMemoryAccessMode` (which matches the MA wire enum verbatim).
 */
export const MEMORY_ACCESS_MODES = ['read_only', 'read_write'];
/** Runtime guard for one {@link MemoryAccessScope}. Exact lowercase literals only. */
export function isMemoryAccessScope(value) {
    return value === 'user' || value === 'firm' || value === 'matter';
}
/** Runtime guard for one {@link MemoryAccessMode}. Exact lowercase literals only. */
export function isMemoryAccessMode(value) {
    return value === 'read_only' || value === 'read_write';
}
/**
 * Bounded, printable echo of a bad key/value for the error message — never
 * reflect an unbounded/hostile authored string back. (Mirrors
 * `describeBadScope` in `./session-memory.ts`.)
 */
function describeBadEntry(value) {
    if (typeof value !== 'string')
        return `a ${typeof value}`;
    const slice = value.length > 32 ? `${value.slice(0, 32)}…` : value;
    return JSON.stringify(slice);
}
/**
 * Validate + normalize an agent-config `memory:` access map. Fail-closed:
 * only the closed scope/mode enumeration passes.
 *
 * Rules (fn-80 R1/R4/R11):
 *   - `undefined` → `{ ok: true, map: undefined }` (field absent — no map).
 *   - anything that is not a plain mapping (incl. `null`, arrays, strings)
 *     → error.
 *   - `{}` → `{ ok: true, map: undefined }` — explicitly empty ≡ absent,
 *     normalized HERE so nothing downstream distinguishes them (R11).
 *   - an unknown scope key → error (closed enumeration; exact lowercase).
 *   - an access value outside `read_only` | `read_write` → error.
 *   - `firm: read_write` → a DISTINCT "not yet supported" error (R4) — the
 *     schema slot exists but cannot be occupied until an admin-gating design
 *     lands; the message tells the author the rejection is deliberate.
 *
 * The returned map lists scopes in CANONICAL ORDER (`user`, `firm`,
 * `matter`) regardless of authored order, so pinned snapshots are
 * byte-stable for a given set of grants.
 */
export function parseMemoryAccessMap(value) {
    if (value === undefined)
        return { ok: true, map: undefined };
    if (typeof value !== 'object' || value === null || Array.isArray(value)) {
        return {
            ok: false,
            error: `field memory must be a mapping of scope (${MEMORY_ACCESS_SCOPES.join(' | ')}) to access mode (${MEMORY_ACCESS_MODES.join(' | ')}) when present`,
        };
    }
    const record = value;
    for (const key of Object.keys(record)) {
        if (!isMemoryAccessScope(key)) {
            return {
                ok: false,
                error: `field memory has unknown scope ${describeBadEntry(key)}; expected one of ${MEMORY_ACCESS_SCOPES.join(', ')}`,
            };
        }
    }
    const map = {};
    // Canonical scope order regardless of authored order — pinned snapshots
    // stay byte-stable for a given set of grants.
    for (const scope of MEMORY_ACCESS_SCOPES) {
        if (!(scope in record))
            continue;
        const mode = record[scope];
        if (!isMemoryAccessMode(mode)) {
            return {
                ok: false,
                error: `field memory.${scope} has unknown access mode ${describeBadEntry(mode)}; expected one of ${MEMORY_ACCESS_MODES.join(', ')}`,
            };
        }
        if (scope === 'firm' && mode === 'read_write') {
            // R4 — deliberate, distinct rejection: firm-wide writable memory needs
            // an admin-gating design before the grant can be occupied.
            return {
                ok: false,
                error: 'field memory.firm: read_write is not yet supported (firm memory stays read-only until an admin-gating design lands)',
            };
        }
        map[scope] = mode;
    }
    // Explicitly-empty ≡ absent (R11): enforced once here so downstream code
    // never distinguishes `{}` from an omitted field.
    if (Object.keys(map).length === 0)
        return { ok: true, map: undefined };
    return { ok: true, map };
}
//# sourceMappingURL=agent-memory-access.js.map